import { useEffect, useMemo, useState } from "react";
import AppShell from "@/components/layout/AppShell";
import api, { fmtError } from "@/lib/api";
import { DataTable, Modal, Btn, Icons, Badge, Field, TextInput } from "@/components/ui-lite";
import { toast } from "sonner";
import { useAuth } from "@/contexts/AuthContext";
import { ShieldCheck, Copy, Check, Plus, Trash2, Pencil } from "lucide-react";

function CopyableAddress({ address }) {
  const [copied, setCopied] = useState(false);
  if (!address) return <span className="text-[var(--wv-text-3)]">—</span>;
  const copy = () => {
    navigator.clipboard.writeText(address).then(() => {
      setCopied(true);
      toast.success("Wallet address copied");
      setTimeout(() => setCopied(false), 1500);
    });
  };
  return (
    <div className="flex items-center gap-1.5">
      <span className="font-mono text-xs truncate max-w-[160px]" title={address}>{address}</span>
      <button data-testid={`pay-copy-addr`} onClick={copy} className="text-[var(--wv-text-2)] hover:text-[var(--wv-primary)]">
        {copied ? <Check size={12}/> : <Copy size={12}/>}
      </button>
    </div>
  );
}

// Returns the list of TX parts for a payment (synthesizes for legacy single-hash payments).
function getParts(p) {
  if (Array.isArray(p?.tx_parts) && p.tx_parts.length > 0) return p.tx_parts;
  if (p?.tx_hash) {
    return [{
      hash: p.tx_hash,
      amount: p.amount,
      currency: p.currency,
      amount_usd: p.amount_usd,
      added_at: p.tx_added_at || p.created_at,
      verification: p.verification || { status: "not_verified" },
    }];
  }
  return [];
}

function sumSettled(p) {
  return getParts(p).reduce((s, x) => s + Number(x.amount || 0), 0);
}

export default function PaymentsPage() {
  const { user } = useAuth();
  const isAdmin = user?.role === "admin";
  const [rows, setRows] = useState([]);
  const [invoices, setInvoices] = useState([]);
  const [detail, setDetail] = useState(null);
  const [scope, setScope] = useState("requested");
  const [txModal, setTxModal] = useState(null);   // payment being edited (attach new part)
  const [txValue, setTxValue] = useState("");
  const [txAmount, setTxAmount] = useState("");
  const [editModal, setEditModal] = useState(null); // { payment, part }
  const [editHash, setEditHash] = useState("");
  const [editAmount, setEditAmount] = useState("");
  const [saving, setSaving] = useState(false);

  const load = () => api.get("/payments").then((r) => setRows(r.data));
  useEffect(() => { load(); api.get("/invoices").then((r) => setInvoices(r.data)); }, []);

  const invById = useMemo(() => Object.fromEntries(invoices.map((i) => [i.id, i])), [invoices]);

  const verify = async (p) => {
    try {
      const { data } = await api.post(`/payments/${p.id}/verify`);
      // Bubble up mismatch reasons in the toast so ops sees them without opening the JSON drawer
      const reasons = new Set();
      for (const part of (data.parts || [])) {
        for (const rr of (part.mismatch_reasons || [])) reasons.add(rr);
      }
      const suffix = reasons.size > 0 ? ` — ${[...reasons].join(", ")} mismatch` : "";
      if (data.status === "verified") toast.success(`Verified on chain${suffix}`);
      else if (data.status === "mismatched") toast.error(`Verification mismatch${suffix} — data doesn't match`);
      else if (data.status === "failed") toast.error("Verification failed — tx not found");
      else toast.warning(`Verification: ${data.status}${suffix}`);
      setDetail(data); load();
    } catch (e) { toast.error(fmtError(e)); }
  };

  const openAttach = (p) => {
    setTxModal(p);
    setTxValue("");
    // Prefill with remaining balance so single-shot payments still Just Work.
    const settled = sumSettled(p);
    const remaining = Math.max(Number(p.amount || 0) - settled, 0);
    setTxAmount(remaining > 0 ? String(remaining) : "");
  };

  const attachTx = async () => {
    if (!txValue.trim()) { toast.error("TX Hash required"); return; }
    const parsed = Number(txAmount);
    if (!parsed || parsed <= 0) { toast.error("Amount must be > 0"); return; }
    setSaving(true);
    try {
      await api.put(`/payments/${txModal.id}/tx`, { tx_hash: txValue.trim(), amount: parsed });
      toast.success("TX attached");
      setTxModal(null); setTxValue(""); setTxAmount(""); load();
    } catch (e) { toast.error(fmtError(e)); }
    finally { setSaving(false); }
  };

  const removePart = async (pid, hash) => {
    if (!window.confirm(`Remove TX ${hash.slice(0, 12)}… from this payment?`)) return;
    try {
      await api.delete(`/payments/${pid}/tx/${encodeURIComponent(hash)}`);
      toast.success("TX removed");
      load();
    } catch (e) { toast.error(fmtError(e)); }
  };

  const openEditPart = (payment, part) => {
    setEditModal({ payment, part });
    setEditHash(part.hash || "");
    setEditAmount(String(part.amount ?? ""));
  };

  const saveEditPart = async () => {
    if (!editHash.trim()) { toast.error("TX hash required"); return; }
    const parsed = Number(editAmount);
    if (!parsed || parsed <= 0) { toast.error("Amount must be > 0"); return; }
    setSaving(true);
    try {
      await api.patch(
        `/payments/${editModal.payment.id}/tx/${encodeURIComponent(editModal.part.hash)}`,
        { new_hash: editHash.trim(), amount: parsed }
      );
      toast.success("TX updated — please re-verify on chain");
      setEditModal(null); setEditHash(""); setEditAmount(""); load();
    } catch (e) { toast.error(fmtError(e)); }
    finally { setSaving(false); }
  };

  const del = async (id) => { if (window.confirm("Delete payment?")) { await api.delete(`/payments/${id}`); load(); } };

  const statusOf = (r) => {
    if (r.status) return r.status;
    return getParts(r).length > 0 ? "paid" : "requested";
  };
  const statusTone = (s) => s === "verified" ? "success" : s === "paid" ? "info" : s === "requested" ? "warning" : "default";

  const filtered = useMemo(() => {
    if (scope === "all") return rows;
    if (scope === "verified") return rows.filter((r) => r.verification?.status === "verified");
    if (scope === "requested") return rows.filter((r) => statusOf(r) === "requested");
    if (scope === "paid_unverified") return rows.filter((r) => statusOf(r) === "paid" && r.verification?.status !== "verified");
    return rows;
  }, [rows, scope]);

  const counts = useMemo(() => ({
    all: rows.length,
    requested: rows.filter((r) => statusOf(r) === "requested").length,
    paid_unverified: rows.filter((r) => statusOf(r) === "paid" && r.verification?.status !== "verified").length,
    verified: rows.filter((r) => r.verification?.status === "verified").length,
  }), [rows]);

  const ScopeBtn = ({ id, label }) => (
    <button data-testid={`pay-scope-${id}`} onClick={() => setScope(id)}
      className={`px-3 py-2 text-sm ${scope === id ? "bg-[var(--wv-primary)] text-white" : "text-[var(--wv-text-2)] hover:bg-white/60"} ${id !== "all" ? "border-l border-[var(--wv-border)]" : ""}`}>
      {label} <span className="ml-1 opacity-75">({counts[id]})</span>
    </button>
  );

  // Progress cell — actual settled vs requested
  const Progress = ({ r }) => {
    const settled = sumSettled(r);
    const req = Number(r.amount || 0);
    const pct = req > 0 ? Math.min(100, Math.round((settled / req) * 100)) : 0;
    const full = settled + 1e-9 >= req && req > 0;
    return (
      <div className="min-w-[140px]">
        <div className="flex items-center justify-between gap-2 text-xs font-mono">
          <span>{settled} / {req} {r.currency}</span>
          <span className={full ? "text-[var(--wv-success)] font-semibold" : "text-[var(--wv-text-3)]"}>{pct}%</span>
        </div>
        <div className="h-1 mt-1 rounded-full bg-white/60 overflow-hidden">
          <div className={`h-full ${full ? "bg-[var(--wv-success)]" : "bg-[var(--wv-primary)]"}`} style={{ width: `${pct}%` }}/>
        </div>
      </div>
    );
  };

  return (
    <AppShell
      title="Payments"
      subtitle="Requested → Paid (add one or more TX hashes) → Verified (on-chain). Multiple partial settlements per payment are supported."
      actions={
        <div className="flex rounded-xl bg-white/70 border border-[var(--wv-border)] overflow-hidden" data-testid="pay-scope">
          <ScopeBtn id="requested" label="Pending payment"/>
          <ScopeBtn id="paid_unverified" label="Paid · unverified"/>
          <ScopeBtn id="verified" label="Verified"/>
          <ScopeBtn id="all" label="All"/>
        </div>
      }
    >
      <DataTable
        testid="payments-table"
        columns={[
          { key: "invoice_id", label: "Invoice", render: (r) => {
            const inv = invById[r.invoice_id];
            if (!inv) return <span className="font-mono text-xs text-[var(--wv-text-3)]">{r.invoice_id?.slice(-6)}</span>;
            return (
              <div className="text-sm">
                <div className="font-mono font-medium">{inv.invoice_number || inv.id.slice(-6)}</div>
                <div className="text-xs text-[var(--wv-text-3)] font-mono">{inv.provider_code} · {inv.month}</div>
              </div>
            );
          } },
          { key: "chain", label: "Chain", render: (r) => <Badge tone={r.chain === "tron" ? "success" : "info"}>{r.chain}</Badge> },
          { key: "amount", label: "Requested", mono: true, render: (r) => `${r.amount} ${r.currency}` },
          { key: "settled", label: "Settled", render: (r) => <Progress r={r}/> },
          { key: "to_address", label: "Wallet", render: (r) => <CopyableAddress address={r.to_address}/> },
          { key: "tx_parts", label: "TX Hashes", render: (r) => {
            const parts = getParts(r);
            if (parts.length === 0) return <span className="text-[var(--wv-text-3)] text-xs">—</span>;
            return (
              <div className="flex flex-col gap-1 max-w-[220px]">
                {parts.map((pt, i) => (
                  <div key={i} className="flex items-center gap-1.5 text-xs">
                    <span className="font-mono truncate" title={pt.hash} data-testid={`pay-part-${r.id}-${i}`}>{pt.hash?.slice(0, 12)}…</span>
                    <span className="text-[var(--wv-text-3)] font-mono">·</span>
                    <span className="font-mono">{Number(pt.amount).toFixed(2)}</span>
                    <span className="text-[var(--wv-text-3)] font-mono">{pt.currency}</span>
                    {isAdmin && (
                      <>
                        <button
                          data-testid={`pay-part-edit-${r.id}-${i}`}
                          onClick={() => openEditPart(r, pt)}
                          title="Edit this TX (wrong hash / amount)"
                          className="text-[var(--wv-text-3)] hover:text-[var(--wv-primary)]"
                        >
                          <Pencil size={11}/>
                        </button>
                        <button
                          data-testid={`pay-part-remove-${r.id}-${i}`}
                          onClick={() => removePart(r.id, pt.hash)}
                          title="Remove this TX"
                          className="text-[var(--wv-text-3)] hover:text-[var(--wv-error)]"
                        >
                          <Trash2 size={11}/>
                        </button>
                      </>
                    )}
                  </div>
                ))}
              </div>
            );
          } },
          { key: "status", label: "Payment", render: (r) => <Badge tone={statusTone(statusOf(r))}>{statusOf(r)}</Badge> },
          { key: "verification", label: "On-chain", render: (r) => {
            const v = r.verification || {};
            const s = v.status || "—";
            const tone =
              s === "verified" ? "success"
              : s === "mismatched" || s === "failed" ? "danger"
              : s === "unconfirmed" || s === "partial" ? "warning"
              : "default";
            // Bubble up the reasons across all parts so ops sees them at a glance
            const reasons = new Set();
            for (const pt of (r.tx_parts || [])) {
              for (const rr of (pt.verification?.mismatch_reasons || [])) reasons.add(rr);
            }
            const label = reasons.size > 0 ? `${s} · ${[...reasons].join(", ")} mismatch` : s;
            return <Badge tone={tone} title={label}>{label}</Badge>;
          } },
          { key: "created_at", label: "Recorded", mono: true, render: (r) => r.created_at ? new Date(r.created_at).toLocaleDateString("en-GB", { dateStyle: "medium" }) : "—" },
        ]}
        rows={filtered}
        actions={isAdmin ? (r) => {
          const parts = getParts(r);
          const settled = sumSettled(r);
          const remaining = Math.max(Number(r.amount || 0) - settled, 0);
          const canAddMore = remaining > 1e-9; // still owe something
          return (
            <div className="flex gap-2 items-center">
              {(parts.length === 0 || canAddMore) && (
                <Btn variant="secondary" data-testid={`pay-attach-tx-${r.id}`} onClick={() => openAttach(r)} className="!py-1 !px-2 text-xs">
                  <Plus size={12} className="inline mr-1"/>
                  {parts.length === 0 ? "Attach TX" : "Add TX"}
                </Btn>
              )}
              {parts.length > 0 && r.verification?.status !== "verified" && (
                <button data-testid={`pay-verify-${r.id}`} onClick={() => verify(r)} title="Verify on chain" className="text-[var(--wv-primary)] hover:opacity-80"><ShieldCheck size={16}/></button>
              )}
              <button onClick={() => del(r.id)} title="Delete" className="text-[var(--wv-text-2)] hover:text-[var(--wv-error)]"><Icons.Trash2 size={16}/></button>
            </div>
          );
        } : null}
      />

      <Modal
        open={!!txModal}
        onClose={() => setTxModal(null)}
        title={txModal ? (getParts(txModal).length > 0 ? "Add another TX to this payment" : "Attach TX hash to payment") : ""}
        footer={<>
          <Btn variant="secondary" onClick={() => setTxModal(null)}>Cancel</Btn>
          <Btn data-testid="pay-attach-tx-save" onClick={attachTx} disabled={saving}>{saving ? "Saving..." : "Add TX"}</Btn>
        </>}
      >
        {txModal && (
          <>
            <div className="mb-3 p-3 rounded-xl bg-[var(--wv-accent-light)]/50 text-sm">
              <div className="font-mono text-xs">{txModal.chain} · Requested: {txModal.amount} {txModal.currency}</div>
              <div className="text-xs mt-1 text-[var(--wv-text-2)]">To: <span className="font-mono">{txModal.to_address}</span></div>
              {getParts(txModal).length > 0 && (
                <div className="mt-2 pt-2 border-t border-white/60">
                  <div className="text-xs font-semibold text-[var(--wv-text-2)] mb-1">Already settled ({getParts(txModal).length} TX):</div>
                  {getParts(txModal).map((pt, i) => (
                    <div key={i} className="text-xs font-mono flex items-center gap-2 text-[var(--wv-text-2)]">
                      <span className="truncate max-w-[220px]" title={pt.hash}>{pt.hash?.slice(0, 20)}…</span>
                      <span>{Number(pt.amount).toFixed(2)} {pt.currency}</span>
                    </div>
                  ))}
                  <div className="mt-1 text-xs">
                    Remaining: <span className="font-mono font-semibold text-[var(--wv-warning)]">
                      {Math.max(Number(txModal.amount) - sumSettled(txModal), 0).toFixed(2)} {txModal.currency}
                    </span>
                  </div>
                </div>
              )}
            </div>
            <Field label="TX Hash from your wallet">
              <TextInput data-testid="pay-attach-tx-input" value={txValue} onChange={(e) => setTxValue(e.target.value.trim())} className="font-mono" placeholder="0x… / btc txid" autoFocus/>
            </Field>
            <Field label={`Amount settled by this TX (${txModal.currency})`}>
              <TextInput data-testid="pay-attach-tx-amount" type="number" step="any" value={txAmount} onChange={(e) => setTxAmount(e.target.value)} className="font-mono" placeholder="e.g. 5000"/>
            </Field>
          </>
        )}
      </Modal>

      <Modal
        open={!!editModal}
        onClose={() => setEditModal(null)}
        title="Edit TX hash"
        footer={<>
          <Btn variant="secondary" onClick={() => setEditModal(null)}>Cancel</Btn>
          <Btn data-testid="pay-edit-tx-save" onClick={saveEditPart} disabled={saving}>{saving ? "Saving..." : "Save"}</Btn>
        </>}
      >
        {editModal && (
          <>
            <div className="mb-3 p-3 rounded-xl bg-[var(--wv-warning-light)]/40 text-xs text-[var(--wv-text-2)]">
              Editing a TX will reset its on-chain verification. Re-verify after saving.
            </div>
            <div className="mb-3 text-xs text-[var(--wv-text-3)]">
              Current: <span className="font-mono">{editModal.part.hash?.slice(0, 24)}…</span> · {Number(editModal.part.amount).toFixed(2)} {editModal.part.currency}
            </div>
            <Field label="New TX Hash">
              <TextInput data-testid="pay-edit-tx-input" value={editHash} onChange={(e) => setEditHash(e.target.value.trim())} className="font-mono" placeholder="0x… / btc txid" autoFocus/>
            </Field>
            <Field label={`Amount (${editModal.part.currency || editModal.payment.currency})`}>
              <TextInput data-testid="pay-edit-tx-amount" type="number" step="any" value={editAmount} onChange={(e) => setEditAmount(e.target.value)} className="font-mono"/>
            </Field>
          </>
        )}
      </Modal>

      <Modal open={!!detail} onClose={() => setDetail(null)} title="Blockchain verification result">
        <pre className="text-xs font-mono bg-[var(--wv-surface-alt)] p-4 rounded-sm overflow-x-auto max-h-96">{JSON.stringify(detail, null, 2)}</pre>
      </Modal>
    </AppShell>
  );
}
